/**
 * API 클라이언트
 * 
 * NestJS 백엔드와 통신하는 Axios 래퍼입니다.
 * 모든 요청에 JWT 토큰을 자동으로 추가합니다.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiClient {
  private token: string | null;

  constructor() {
    this.token = localStorage.getItem('jwtToken');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('jwtToken', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('jwtToken');
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
    };
  }

  async request(
    method: string,
    endpoint: string,
    data?: any,
  ): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`🔵 API Request: ${method} ${url}`, data);

    try {
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      console.log(`🟢 API Response: ${response.status} ${response.statusText}`);

      if (response.status === 401) {
        // 토큰 만료
        this.clearToken();
        window.location.href = '/login';
        throw new Error('Token expired');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`🔴 API Error Response:`, errorText);
        let errorMessage = response.statusText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log(`✅ API Success:`, result);
      return result;
    } catch (error) {
      console.error(`❌ API request failed: ${method} ${endpoint}`, error);
      throw error;
    }
  }

  // Auth
  async login(username: string, password: string) {
    return this.request('POST', '/api/auth/login', { username, password });
  }

  async logout() {
    return this.request('POST', '/api/auth/logout');
  }

  // Profile
  async getProfile() {
    return this.request('GET', '/api/auth/profile');
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request('PATCH', '/api/auth/profile/password', {
      currentPassword,
      newPassword,
    });
  }

  async uploadPhoto(photoBase64: string) {
    return this.request('POST', '/api/auth/profile/photo', {
      photo: photoBase64,
    });
  }

  async removePhoto() {
    return this.request('DELETE', '/api/auth/profile/photo');
  }

  // Students
  async getStudents() {
    return this.request('GET', '/api/students');
  }

  async getStudent(id: number) {
    return this.request('GET', `/api/students/${id}`);
  }

  async createStudent(data: any) {
    return this.request('POST', '/api/students', data);
  }

  async updateStudent(id: string | number, data: any) {
    return this.request('PUT', `/api/students/${id}`, data);
  }

  async deleteStudent(id: string | number) {
    return this.request('DELETE', `/api/students/${id}`);
  }

  // History (deprecated - use getStudentsHistory or getPointsHistory)
  async getHistory(filters?: any) {
    const params = new URLSearchParams();
    if (filters?.studentNumber) params.append('studentNumber', filters.studentNumber);
    if (filters?.name) params.append('name', filters.name);
    if (filters?.grade) params.append('grade', filters.grade);
    if (filters?.class) params.append('class', filters.class);
    if (filters?.performedBy) params.append('performedBy', filters.performedBy);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request('GET', `/api/history${query}`);
  }

  // Students History
  async getStudentsHistory(filters?: any) {
    const params = new URLSearchParams();
    if (filters?.studentNumber) params.append('studentNumber', filters.studentNumber);
    if (filters?.grade) params.append('grade', filters.grade);
    if (filters?.class) params.append('class', filters.class);
    if (filters?.actionType) params.append('actionType', filters.actionType);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request('GET', `/api/history/students${query}`);
  }

  // Points History
  async getPointsHistory(filters?: any) {
    const params = new URLSearchParams();
    if (filters?.studentNumber) params.append('studentNumber', filters.studentNumber);
    if (filters?.grade) params.append('grade', filters.grade);
    if (filters?.class) params.append('class', filters.class);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request('GET', `/api/points-history${query}`);
  }

  async getPointsHistoryByStudent(studentNumber: string) {
    return this.request('GET', `/api/points-history/student?studentNumber=${studentNumber}`);
  }

  // Classes
  async getClasses() {
    return this.request('GET', '/api/classes');
  }

  async getClass(id: number) {
    return this.request('GET', `/api/classes/${id}`);
  }

  async createClass(data: any) {
    return this.request('POST', '/api/classes', data);
  }

  async updateClass(id: string | number, data: any) {
    return this.request('PUT', `/api/classes/${id}`, data);
  }

  async deleteClass(id: string | number) {
    return this.request('DELETE', `/api/classes/${id}`);
  }

  async getClassTeachers() {
    return this.request('GET', '/api/classes/data/teachers');
  }

  async getTeachingTeachers(): Promise<{ id: number; username: string }[]> {
    const res = await this.request('GET', '/api/rbac/roles/by-name/teaching_teacher/users');
    return (res?.users || []) as { id: number; username: string }[];
  }

  async getClassClassrooms() {
    return this.request('GET', '/api/classes/data/classrooms');
  }

  // Users (Admin)
  async getUsers() {
    return this.request('GET', '/api/users');
  }

  async getUser(id: number) {
    return this.request('GET', `/api/users/${id}`);
  }

  async createUser(username: string, password: string, role: 'admin' | 'teacher' | 'viewer') {
    return this.request('POST', '/api/users', {
      username,
      password,
      role,
    });
  }

  async deleteUser(id: number) {
    return this.request('DELETE', `/api/users/${id}`);
  }

  async updateUserPassword(id: number, password: string) {
    return this.request('PATCH', `/api/users/${id}/password`, { password });
  }

  // Attendance
  async checkin(studentNumber: string) {
    return this.request('POST', '/api/attendance/checkin', {
      studentNumber,
    });
  }

  async checkout(studentNumber: string) {
    return this.request('POST', '/api/attendance/checkout', {
      studentNumber,
    });
  }

  async setAttendanceStatus(studentNumber: string, attendance: string, timestamp?: string) {
    return this.request('POST', '/api/attendance/status', {
      studentNumber,
      attendance,
      timestamp,
    });
  }

  async getAttendanceHistory(filters?: any) {
    const params = new URLSearchParams();
    if (filters?.studentNumber) params.append('studentNumber', filters.studentNumber);
    if (filters?.name) params.append('name', filters.name);
    if (filters?.grade) params.append('grade', filters.grade);
    if (filters?.class) params.append('class', filters.class);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request('GET', `/api/attendance/history${query}`);
  }

  async processCompensation(
    historyId: number,
    compensationType: 'refund' | 'supplement',
    compensationClassId?: number
  ) {
    return this.request('PUT', `/api/attendance/history/${historyId}/compensation`, {
      compensationType,
      compensationClassId,
    });
  }

  // Face Recognition
  async saveFaceDescriptor(studentNumber: string, descriptor: number[]) {
    return this.request('POST', '/api/students/face-descriptor', {
      studentNumber,
      descriptor,
    });
  }

  async getFaceDescriptor(studentNumber: string) {
    return this.request('GET', `/api/students/${studentNumber}/face-descriptor`);
  }

  async getAllFaceDescriptors() {
    return this.request('GET', '/api/students/face-descriptors/all');
  }

  // RBAC - Permissions & Roles Management
  async getMyPermissions() {
    return this.request('GET', '/api/rbac/me/permissions');
  }

  async getMyRoles() {
    return this.request('GET', '/api/rbac/me/roles');
  }

  async getAllRoles() {
    return this.request('GET', '/api/rbac/roles');
  }

  async getAllPermissions() {
    return this.request('GET', '/api/rbac/permissions');
  }

  async getRolePermissions(roleId: number) {
    return this.request('GET', `/api/rbac/roles/${roleId}/permissions`);
  }

  async assignRoleToUser(userId: number, roleId: number) {
    return this.request('POST', `/api/rbac/users/${userId}/roles`, { roleId });
  }

  async removeRoleFromUser(userId: number, roleId: number) {
    return this.request('DELETE', `/api/rbac/users/${userId}/roles/${roleId}`);
  }

  async assignPermissionToRole(roleId: number, permissionId: number) {
    return this.request('POST', `/api/rbac/roles/${roleId}/permissions`, { permissionId });
  }

  async removePermissionFromRole(roleId: number, permissionId: number) {
    return this.request('DELETE', `/api/rbac/roles/${roleId}/permissions/${permissionId}`);
  }

  async setRoleHierarchy(parentRoleId: number, childRoleId: number) {
    return this.request('POST', `/api/rbac/roles/${parentRoleId}/inherit/${childRoleId}`);
  }

  async removeRoleHierarchy(parentRoleId: number, childRoleId: number) {
    return this.request('DELETE', `/api/rbac/roles/${parentRoleId}/inherit/${childRoleId}`);
  }

  async toggleUserRBAC(userId: number, useRBAC: boolean) {
    return this.request('PATCH', `/api/users/${userId}/rbac`, { use_rbac: useRBAC });
  }

  // Compensation (결석 보상 관리)
  async getCompensations(filters?: any) {
    const params = new URLSearchParams();
    if (filters?.studentNumber) params.append('studentNumber', filters.studentNumber);
    if (filters?.name) params.append('name', filters.name);
    if (filters?.grade) params.append('grade', filters.grade);
    if (filters?.class) params.append('class', filters.class);
    if (filters?.compensationType) params.append('compensationType', filters.compensationType);
    if (filters?.isCompleted !== undefined) params.append('isCompleted', filters.isCompleted.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request('GET', `/api/compensation${query}`);
  }

  async createCompensationFromAbsence(attendanceHistoryId: number) {
    return this.request('POST', '/api/compensation/from-absence', {
      attendanceHistoryId,
    });
  }

  async updateCompensation(
    id: number,
    data: {
      compensationType?: string;
      supplementClassId?: number | null;
      creditAmount?: number | null;
      detailReason?: string | null;
      isCompleted?: boolean;
    }
  ) {
    return this.request('PUT', `/api/compensation/${id}`, data);
  }

  async deleteCompensation(id: number) {
    return this.request('DELETE', `/api/compensation/${id}`);
  }

  async syncCompensations() {
    return this.request('POST', '/api/compensation/sync');
  }

  async sendMessage(
    receiver: string,
    msg: string,
    testmode = false,
    extra?: { teacherName?: string; className?: string; studentName?: string; recipientLabel?: string },
  ) {
    return this.request('POST', '/api/message/send', { receiver, msg, testmode, ...extra });
  }

  async getMessageRemain() {
    return this.request('GET', '/api/message/remain');
  }

  async sendMessageBulk(
    recipients: { receiver: string; teacherName?: string; className?: string; studentName?: string; recipientLabel?: string }[],
    msg: string,
    testmode = false,
  ) {
    return this.request('POST', '/api/message/send-bulk', { recipients, msg, testmode });
  }

  async getMessageHistory(params?: { teacherName?: string; className?: string; studentName?: string }) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][]).toString()
      : '';
    return this.request('GET', `/api/message/history${qs}`);
  }

  async getAlimtalkHistory(params?: { studentName?: string; success?: string }) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][]).toString()
      : '';
    return this.request('GET', `/api/kakao-alimtalk/history${qs}`);
  }

  // ── 메시지 템플릿 ──
  async getMessageTemplates(): Promise<{ id: number; templateName: string; templateContent: string; createdAt: string; updatedAt: string }[]> {
    return this.request('GET', '/api/message-templates');
  }

  async createMessageTemplate(templateName: string, templateContent: string) {
    return this.request('POST', '/api/message-templates', { templateName, templateContent });
  }

  async updateMessageTemplate(id: number, templateName: string, templateContent: string) {
    return this.request('PUT', `/api/message-templates/${id}`, { templateName, templateContent });
  }

  async deleteMessageTemplate(id: number) {
    return this.request('DELETE', `/api/message-templates/${id}`);
  }

  // ── 알림톡 템플릿 ──
  async getAlimtalkTemplates(): Promise<{ id: number; tplCode: string; tplName: string; tplContent: string; createdAt: string; updatedAt: string }[]> {
    return this.request('GET', '/api/alimtalk-templates');
  }

  async createAlimtalkTemplate(tplCode: string, tplName: string, tplContent: string) {
    return this.request('POST', '/api/alimtalk-templates', { tplCode, tplName, tplContent });
  }

  async updateAlimtalkTemplate(id: number, tplCode: string, tplName: string, tplContent: string) {
    return this.request('PUT', `/api/alimtalk-templates/${id}`, { tplCode, tplName, tplContent });
  }

  async deleteAlimtalkTemplate(id: number) {
    return this.request('DELETE', `/api/alimtalk-templates/${id}`);
  }

  // ── 출결 알림톡 이벤트-템플릿 연결 ──
  async getAttendanceAlimtalkEvents(): Promise<
    { eventType: string; templateId: number | null; template: { id: number; tplCode: string; tplName: string; tplContent: string } | null }[]
  > {
    return this.request('GET', '/api/attendance-alimtalk-events');
  }

  async setAttendanceAlimtalkEvent(
    eventType: string,
    templateId: number | null,
  ): Promise<void> {
    await this.request('PUT', `/api/attendance-alimtalk-events/${encodeURIComponent(eventType)}`, {
      templateId,
    });
  }

  // Settings
  async getSettings(): Promise<Record<string, string | null>> {
    return this.request('GET', '/api/settings');
  }

  async setSetting(key: string, value: string | null): Promise<void> {
    await this.request('PUT', `/api/settings/${key}`, { value });
  }

  async uploadSettingsLogo(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/api/settings/logo`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || response.statusText);
    }

    const result = await response.json();
    return result.url as string;
  }

  async removeSettingsLogo(): Promise<void> {
    await this.request('DELETE', '/api/settings/logo');
  }
}

export const apiClient = new ApiClient();
